import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Routes, Route } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import {
  ContactInfo,
  BackgroundAndEducation,
  PastExperience,
  Demographic,
  EducationQuestions,
  ContactSupport,
} from 'features/profile';
import { Demographic as RegisterDemographic } from 'features/register';
import { Accordion, Subheader, PageNotFound } from 'shared/components';
import * as userActions from 'core/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import { PersonnelModel } from 'core/userModel';
import Auth from 'core/services/authToken';
import { Base64 } from 'js-base64';
import './ProfileContainer.scss';

const CONTACT_INFO = 'contact';
const DEMOGRAPHIC_INFO = 'demographic';
const BACKGROUND_INFO = 'background';
const EDUCATION_INFO = 'education';
const PAST_EXPERIENCE = 'experience';

class ProfileContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      startPosition: 0,
      user: {
        ...props.currentUser,
      },
    };

    this.handleBack = this.handleBack.bind(this);
    this.onSave = this.onSave.bind(this);
    this.renderContactInfo = this.renderContactInfo.bind(this);
    this.renderPastExperience = this.renderPastExperience.bind(this);
    this.renderDemographic = this.renderDemographic.bind(this);
    this.renderBackgroundInfo = this.renderBackgroundInfo.bind(this);
    this.renderEducation = this.renderEducation.bind(this);
    this.setEditableSection = this.setEditableSection.bind(this);
    this.renderContainer = this.renderContainer.bind(this);
    this.renderPersonalInformation = this.renderPersonalInformation.bind(this);
  }

  componentDidUpdate(prevProps) {
    const currentUser = this.props.currentUser ? this.props.currentUser : {};

    if (
      !this.props.errorUpdateUser &&
      !this.props.isLoadingUpdatedUser &&
      (prevProps.errorUpdateUser !== this.props.errorUpdateUser ||
        prevProps.isLoadingUpdatedUser !== this.props.isLoadingUpdatedUser)
    ) {
      this.setState({
        user: {
          ...currentUser,
        },
      });
    }
  }

  async onSave(userInfo, position) {
    let previouslyAdministeredTestValue = [];
    if (
      Array.isArray(userInfo.previouslyAdministeredTests) &&
      userInfo.previouslyAdministeredTests.length
    ) {
      previouslyAdministeredTestValue = userInfo.previouslyAdministeredTests
        .map((test) => test.replaceAll(/\s/g, '').length > 0 && test)
        .filter(Boolean);
    } else {
      previouslyAdministeredTestValue =
        userInfo.previouslyAdministeredTests.replaceAll(/\s/g, '').split(',') || [];
    }

    const newUser = {
      ...userInfo,
      previouslyAdministeredTests: previouslyAdministeredTestValue,
    };
    this.setState({
      user: newUser,
    });

    const personnel = new PersonnelModel(newUser);
    personnel.id = this.props.currentUser.id;

    if (!personnel.id) {
      const tokken = Auth.authToken.split(' ')[0].split('.')[1];
      const userEmail = JSON.parse(Base64.decode(tokken)).email;
      await this.props.actions.createUserInfo({
        ...personnel,
        emailAddress: userEmail,
      });
    } else {
      const newPersonnel = personnel;
      delete newPersonnel.testCenterCyclePersonnels;
      delete newPersonnel.testCenterEventPersonnels;
      await this.props.actions.updateUserInfo(newPersonnel);
    }

    if (this.props.errorUpdateUser) {
      return;
    }

    switch (this.props.location.pathname) {
      case '/profile/personal-information':
        this.props.history.push('/dashboard');
        break;
      default:
        this.handleBack(position);
    }
  }

  setEditableSection(content) {
    switch (content) {
      case CONTACT_INFO:
        this.props.history.push('/profile/update-contact-info');
        break;
      case DEMOGRAPHIC_INFO:
        this.props.history.push('/profile/demographic-info');
        break;
      case BACKGROUND_INFO:
        this.props.history.push('/profile/background-info');
        break;
      case EDUCATION_INFO:
        this.props.history.push('/profile/education-info');
        break;
      case PAST_EXPERIENCE:
        this.props.history.push('/profile/past-experience');
        break;
      default:
        return null;
    }
  }

  handleBack(startPosition) {
    if (this.props.location.pathname === '/profile/personal-information') {
      this.props.history.push('/dashboard');
    } else {
      this.props.actions.clearUserError();
      this.setState({
        startPosition,
      });
      this.props.history.push('/profile');
    }
  }

  renderContactInfo() {
    return (
      <ContactInfo
        editInfo
        user={this.state.user}
        cancel={this.handleBack}
        onSave={this.onSave}
        serverError={this.props.errorUpdateUser}
      />
    );
  }

  renderPersonalInformation() {
    return (
      <ContactInfo
        editInfo
        reviewInfo={false}
        cancel={this.handleBack}
        user={this.state.user}
        onSave={this.onSave}
        serverError={this.props.errorUpdateUser}
      />
    );
  }

  renderDemographic() {
    return <Demographic user={this.state.user} onBack={this.handleBack} onConfirm={this.onSave} />;
  }

  renderBackgroundInfo() {
    return (
      <RegisterDemographic
        reviewDemographic
        user={this.state.user}
        onCancel={this.handleBack}
        onSubmit={this.onSave}
      />
    );
  }

  renderEducation() {
    return (
      <EducationQuestions
        user={this.state.user}
        onCancel={this.handleBack}
        onSubmit={this.onSave}
      />
    );
  }

  renderPastExperience() {
    return (
      <PastExperience
        editInfo
        user={this.state.user}
        cancel={this.handleBack}
        onSave={this.onSave}
      />
    );
  }

  renderContainer() {
    const userWithAddress = {
      ...this.props.currentUser,
    };
    return (
      <div className="inside-container">
        <h4>{[this.state.user.lastName, this.state.user.firstName].filter(Boolean).join(', ')}</h4>
        <Accordion startPosition={this.state.startPosition}>
          <div data-trigger={this.props.t('profile.contactInfo.title')}>
            <ContactInfo user={userWithAddress} onEdit={this.setEditableSection} />
          </div>
          <div data-trigger={this.props.t('profile.background.title')}>
            <BackgroundAndEducation
              user={this.props.currentUser}
              races={this.props.races}
              onEdit={this.setEditableSection}
            />
          </div>
          <div data-trigger={this.props.t('profile.pastExperience.title')}>
            <PastExperience user={this.props.currentUser} onEdit={this.setEditableSection} />
          </div>
          <div data-trigger={this.props.t('profile.contactSupport.title')}>
            <ContactSupport />
          </div>
        </Accordion>
        <div className="mt-3">
          <p className="mb-2">{this.props.t('profile.logOutQuestion')}</p>
          <button className="btn btn-small btn-outline m-0" onClick={this.props.actions.logoutUser}>
            {this.props.t('common.labels.logOutNow')}
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { pathname } = this.props.location;
    const isRootPath = pathname === '/profile' || pathname === '/profile/';

    return (
      <div className="col ">
        {!isRootPath && !['/profile/personal-information'].includes(pathname) ? (
          <Subheader className="">
            <div className="profile-subheader pt-0 pb-0">
              <span className="text-strong">{this.props.t('profile.subTitle')}</span>
            </div>
          </Subheader>
        ) : null}
        <div className="profile-container pt-3">
          <Routes>
            <Route index element={this.renderContainer()} />
            <Route path="update-contact-info" element={this.renderContactInfo()} />
            <Route path="demographic-info" element={this.renderDemographic()} />
            <Route path="background-info" element={this.renderBackgroundInfo()} />
            <Route path="education-info" element={this.renderEducation()} />
            <Route path="past-experience" element={this.renderPastExperience()} />
            <Route path="personal-information" element={this.renderPersonalInformation()} />
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </div>
      </div>
    );
  }
}

ProfileContainer.propTypes = {
  // Props from react-router (via withRouterLegacy)
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,

  // Props from mapStateToProps
  users: PropTypes.shape({
    currentUser: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    userLoaded: PropTypes.bool,
  }),
  currentUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lastName: PropTypes.string,
    firstName: PropTypes.string,
    testCenterCyclePersonnels: PropTypes.array,
    testCenterEventPersonnels: PropTypes.array,
  }).isRequired,
  isLoadingUpdatedUser: PropTypes.bool,
  errorUpdateUser: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  races: PropTypes.array,

  // Props from mapDispatchToProps
  actions: PropTypes.shape({
    updateUserInfo: PropTypes.func.isRequired,
    createUserInfo: PropTypes.func.isRequired,
    logoutUser: PropTypes.func.isRequired,
    clearUserError: PropTypes.func.isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    users: state.users || {},
    currentUser: state.users.currentUser,
    isLoadingUpdatedUser: state.users.isLoadingUpdate,
    errorUpdateUser: state.users.errorUpdateUser,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...userActions, ...staticActions }, dispatch),
  };
}

import { withRouterLegacy } from 'core/routerUtils';

export default withTranslation()(
  withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(ProfileContainer))
);
